import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  getPublicDeclaredLoadTracking,
  PublicTrackingUnavailableError,
} from '../services/api/publicTracking';
import { subscribeToPublicDeclaredLoadTracking } from '../services/publicTrackingSocket';
import type { PublicDeclaredLoadTracking } from '../types/publicTracking';

const ACTIVE_REFRESH_MS = 8_000;
const FINISHED_REFRESH_MS = 30_000;
const FINISHED_STATUSES = new Set(['COMPLETED', 'DELIVERED', 'CANCELLED', 'CANCELLED_BY_SYSTEM', 'EXPIRED']);

interface PublicTrackingState {
  data: PublicDeclaredLoadTracking | null;
  loading: boolean;
  refreshing: boolean;
  missing: boolean;
  expired: boolean;
  error: string | null;
  connected: boolean;
  lastLoadedAt: Date | null;
  refresh: () => Promise<void>;
}

function isFinished(status?: string | null): boolean {
  return !!status && FINISHED_STATUSES.has(status);
}

export function usePublicDeclaredLoadTracking(token?: string): PublicTrackingState {
  const [data, setData] = useState<PublicDeclaredLoadTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [missing, setMissing] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (silent = false, signal?: AbortSignal) => {
      if (!token) {
        setLoading(false);
        setMissing(true);
        setError('This tracking link is missing its token.');
        return;
      }

      if (!silent) setLoading(true);
      if (silent) setRefreshing(true);

      try {
        const next = await getPublicDeclaredLoadTracking(token, { signal });
        if (!mountedRef.current) return;

        setData(next);
        setMissing(false);
        setExpired(false);
        setError(null);
        setLastLoadedAt(new Date());
      } catch (err) {
        if (!mountedRef.current || axios.isCancel(err)) return;

        if (err instanceof PublicTrackingUnavailableError) {
          setMissing(err.status === 404);
          setExpired(err.status === 410);
          setError(err.message);
        } else {
          setError('Tracking is temporarily unavailable.');
        }
      } finally {
        if (!mountedRef.current) return;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(false, controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!token || missing || expired) return undefined;

    const interval = window.setInterval(() => {
      void load(true);
    }, isFinished(data?.status) ? FINISHED_REFRESH_MS : ACTIVE_REFRESH_MS);

    return () => window.clearInterval(interval);
  }, [data?.status, expired, load, missing, token]);

  useEffect(() => {
    const channelId = data?.publicChannelId;
    if (!channelId) {
      setConnected(false);
      return undefined;
    }

    return subscribeToPublicDeclaredLoadTracking(channelId, {
      onSnapshot: (snapshot) => {
        setData(snapshot);
        setMissing(false);
        setExpired(false);
        setError(null);
        setLastLoadedAt(new Date());
      },
      onConnectionChange: setConnected,
    });
  }, [data?.publicChannelId]);

  const refresh = useCallback(() => load(true), [load]);

  return { data, loading, refreshing, missing, expired, error, connected, lastLoadedAt, refresh };
}
